import type { LoaderFunctionArgs, MetaFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import { Link, useLoaderData } from '@remix-run/react';
import { buildMeta } from '~/lib/seo';
import { routes } from '~/lib/routes';
import { getSkillById } from '~/lib/game-data.server';
import { H1, H2, Lead } from '~/components/ui/typography';
import { Container } from '~/components/ui/container';

export const loader = async ({ params }: LoaderFunctionArgs) => {
  const { skillId } = params;

  if (!skillId) {
    throw new Response('Skill ID is required', { status: 400 });
  }

  const skill = getSkillById(skillId);

  if (!skill) {
    throw new Response('Skill not found', { status: 404 });
  }

  return json({ skill });
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data) {
    return buildMeta({
      title: 'Skill Not Found',
      description: 'The requested skill could not be found.',
    });
  }

  return buildMeta({
    title: `${data.skill.name} - Skills Database`,
    description: data.skill.description,
  });
};

export default function SkillDetailPage() {
  const { skill } = useLoaderData<typeof loader>();

  // Helper function to capitalize class names for display
  const capitalizeClassName = (name: string) => {
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  return (
    <Container size="default" padding="large" className="space-y-8">
      <header className="space-y-3">
        <Link
          to={routes.database.skills()}
          className="inline-flex items-center text-sm font-semibold text-primary hover:underline"
        >
          ← Back to Skills
        </Link>
        <div className="flex items-center gap-2">
          <span
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              skill.wheel_type === 'physical'
                ? 'bg-orange-500/10 text-orange-500'
                : 'bg-blue-500/10 text-blue-500'
            }`}
          >
            {skill.wheel_type === 'physical' ? 'Physical' : 'Magic'}
          </span>
          <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
            {skill.skill_category}
          </span>
          {skill.is_master_skill && (
            <span className="rounded-full bg-yellow-500/10 px-3 py-1 text-xs font-semibold text-yellow-500">
              Master Skill
            </span>
          )}
        </div>
        <H1>{skill.name}</H1>
        <Lead>{skill.description}</Lead>
      </header>

      {/* Core Information */}
      <div className="rounded-lg border border-border bg-card p-6 shadow">
        <H2 className="mb-4">Core Information</H2>
        <dl className="grid gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Class</dt>
            <dd className="mt-1 text-base text-foreground">{capitalizeClassName(skill.class)}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Specialization</dt>
            <dd className="mt-1 text-base text-foreground capitalize">
              {skill.specialization && skill.specialization.trim() !== '' ? skill.specialization : '-'}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Range Type</dt>
            <dd className="mt-1 text-base text-foreground capitalize">{skill.range_type}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Max Level</dt>
            <dd className="mt-1 text-base text-foreground">{skill.max_level}</dd>
          </div>
        </dl>
      </div>

      {/* Combat Stats */}
      <div className="rounded-lg border border-border bg-card p-6 shadow">
        <H2 className="mb-4">Combat Stats</H2>
        <dl className="grid gap-4 sm:grid-cols-2">
          {skill.base_damage > 0 && (
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Base Damage</dt>
              <dd className="mt-1 text-base text-foreground">{skill.base_damage}</dd>
            </div>
          )}
          {skill.cooldown > 0 && (
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Cooldown</dt>
              <dd className="mt-1 text-base text-foreground">{skill.cooldown}s</dd>
            </div>
          )}
          {skill.cast_time > 0 && (
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Cast Time</dt>
              <dd className="mt-1 text-base text-foreground">{skill.cast_time}s</dd>
            </div>
          )}
          {skill.stamina_cost !== undefined && skill.stamina_cost > 0 && (
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Stamina Cost</dt>
              <dd className="mt-1 text-base text-foreground">{skill.stamina_cost}</dd>
            </div>
          )}
          {skill.mana_cost !== undefined && skill.mana_cost > 0 && (
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Mana Cost</dt>
              <dd className="mt-1 text-base text-foreground">{skill.mana_cost}</dd>
            </div>
          )}
          {skill.health_cost !== undefined && skill.health_cost > 0 && (
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Health Cost</dt>
              <dd className="mt-1 text-base text-foreground">{skill.health_cost}</dd>
            </div>
          )}
          {skill.can_critical_hit !== undefined && (
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Can Critical Hit</dt>
              <dd className="mt-1 text-base text-foreground">
                {skill.can_critical_hit ? 'Yes' : 'No'}
              </dd>
            </div>
          )}
          {skill.can_interrupt && (
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Can Interrupt</dt>
              <dd className="mt-1 text-base text-foreground">Yes</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Damage Types */}
      {skill.damage_type && skill.damage_type.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-6 shadow">
          <H2 className="mb-4">Damage Types</H2>
          <div className="flex flex-wrap gap-2">
            {skill.damage_type.map((type) => (
              <span
                key={type}
                className="rounded-full border border-border bg-background px-3 py-1 text-sm font-medium text-foreground"
              >
                {type}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Status Effects */}
      {skill.status_effects && skill.status_effects.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-6 shadow">
          <H2 className="mb-4">Status Effects</H2>
          <div className="space-y-3">
            {skill.status_effects.map((effect, index) => (
              <div key={index} className="rounded-lg border border-border bg-background p-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-foreground">{effect.effect_id}</span>
                  <span className="text-sm text-muted-foreground">
                    {effect.application_chance}% chance
                  </span>
                </div>
                <div className="mt-1 text-sm text-muted-foreground">Degree: {effect.degree}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scaling */}
      {skill.level_scaling && (
        <div className="rounded-lg border border-border bg-card p-6 shadow">
          <H2 className="mb-4">Level Scaling</H2>
          <dl className="grid gap-4 sm:grid-cols-2">
            {skill.level_scaling.damage_per_level !== undefined && (
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Damage per Level</dt>
                <dd className="mt-1 text-base text-foreground">
                  +{skill.level_scaling.damage_per_level}
                </dd>
              </div>
            )}
            {skill.level_scaling.cooldown_reduction_per_level !== undefined && (
              <div>
                <dt className="text-sm font-medium text-muted-foreground">
                  Cooldown Reduction per Level
                </dt>
                <dd className="mt-1 text-base text-foreground">
                  -{skill.level_scaling.cooldown_reduction_per_level}s
                </dd>
              </div>
            )}
            {skill.level_scaling.duration_increase_per_level !== undefined && (
              <div>
                <dt className="text-sm font-medium text-muted-foreground">
                  Duration Increase per Level
                </dt>
                <dd className="mt-1 text-base text-foreground">
                  +{skill.level_scaling.duration_increase_per_level}s
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}

      {/* Attributes */}
      <div className="rounded-lg border border-border bg-card p-6 shadow">
        <H2 className="mb-4">Additional Attributes</H2>
        <dl className="grid gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Easily Interruptible</dt>
            <dd className="mt-1 text-base text-foreground">
              {skill.easily_interruptible ? 'Yes' : 'No'}
            </dd>
          </div>
          {skill.primary_scaling_attribute && (
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Primary Scaling Attribute
              </dt>
              <dd className="mt-1 text-base text-foreground capitalize">
                {skill.primary_scaling_attribute}
              </dd>
            </div>
          )}
          {skill.required_weapon && (
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Required Weapon</dt>
              <dd className="mt-1 text-base text-foreground capitalize">
                {skill.required_weapon}
              </dd>
            </div>
          )}
        </dl>
      </div>
    </Container>
  );
}
